import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, FileDown, Save, FileSpreadsheet, FileText, Stethoscope } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const CERT_ORDER = ["compte_rendu", "coups_blessures", "arret_travail", "reprise_service", "grossesse", "accouchement", "naissance"];

function blobDownload(res, name) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

/* Section générique pour un type de document (table + formulaire dynamique) */
function DocSection({ doctype, schema }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id} ou null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/soins/docs?doctype=${doctype}`); setDocs(data.docs); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [doctype]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...d.data }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/soins/docs/${editing.id}`, { doctype, data: form });
      else await api.post("/admin/soins/docs", { doctype, data: form });
      toast.success("Document enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/admin/soins/docs/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const exportPdf = async (id) => {
    try { const res = await api.get(`/admin/soins/docs/${id}/pdf`, { responseType: "blob" }); blobDownload(res, `${doctype}-${id}.pdf`); toast.success("PDF généré"); }
    catch { toast.error("Export PDF impossible"); }
  };
  const saveAndPdf = async () => { await save(); };

  return (
    <div data-testid={`soins-section-${doctype}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-navy">{schema.label}</h3>
        <Button size="sm" onClick={openNew} data-testid={`soins-add-${doctype}`} className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]"><Plus size={14} className="mr-1" /> Ajouter</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="px-4 py-2">Référence / Patient</th><th className="px-4 py-2">Modifié le</th><th className="px-4 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-[#0E7C7B]" /></td></tr>
            ) : docs.length === 0 ? (
              <tr><td colSpan={3} className="py-6 text-center text-xs text-slate-400">Aucune entrée. Cliquez sur « Ajouter ».</td></tr>
            ) : docs.map((d) => (
              <tr key={d.id} className="border-t border-slate-100" data-testid={`soins-row-${d.id}`}>
                <td className="px-4 py-2 font-medium text-navy">{d.resume}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{d.updated_at ? new Date(d.updated_at).toLocaleString("fr-FR") : "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => openEdit(d)} data-testid={`soins-edit-${d.id}`} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-ciel"><Pencil size={15} /></button>
                    <button onClick={() => exportPdf(d.id)} data-testid={`soins-pdf-${d.id}`} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><FileDown size={15} /></button>
                    <button onClick={() => remove(d.id)} data-testid={`soins-del-${d.id}`} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid={`soins-dialog-${doctype}`}>
          <DialogHeader><DialogTitle>{schema.label}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {schema.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="text-[11px] font-medium text-slate-500">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea rows={3} value={form[f.key] || ""} data-testid={`soins-field-${f.key}`}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1 text-sm" />
                ) : (
                  <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] || ""} data-testid={`soins-field-${f.key}`}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1 h-9 text-sm" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button onClick={save} disabled={saving} data-testid={`soins-save-${doctype}`} className="rounded-full bg-navy text-white">
              {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Save size={15} className="mr-1" />} Enregistrer
            </Button>
            {editing && (
              <Button variant="outline" onClick={() => exportPdf(editing.id)} data-testid={`soins-dialog-pdf-${doctype}`} className="rounded-full">
                <FileDown size={15} className="mr-1" /> Exporter en PDF
              </Button>
            )}
          </div>
          {!editing && <p className="text-[11px] text-slate-400">Enregistrez d'abord le document pour pouvoir l'exporter en PDF.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Grille tarifaire GHM/GHS éditable */
export function GhmGrid() {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/soins/ghm"); setRows(data.rows); } catch (e) { toast.error(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (i, k, v) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows([...rows, { code_ghm: "", libelle: "", ghs_fcfa: 0, borne_basse: 0, borne_haute: 0 }]);
  const delRow = (i) => setRows(rows.filter((_, j) => j !== i));
  const save = async () => { setSaving(true); try { const { data } = await api.put("/admin/soins/ghm", { rows }); setRows(data.rows); toast.success("Grille enregistrée"); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } };
  const exp = async (fmt) => { try { const res = await api.get(`/admin/soins/ghm/export?format=${fmt}`, { responseType: "blob" }); blobDownload(res, `grille-ghm-ghs.${fmt}`); toast.success("Export généré"); } catch { toast.error("Export impossible"); } };

  return (
    <div data-testid="soins-ghm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold text-navy">GHM / GHS — Grille tarifaire (FCFA)</h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={addRow} data-testid="soins-ghm-add" className="rounded-full text-xs"><Plus size={13} className="mr-1" /> Ligne</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="soins-ghm-save" className="rounded-full bg-navy text-xs text-white">{saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />} Enregistrer</Button>
          <Button size="sm" variant="outline" onClick={() => exp("pdf")} data-testid="soins-ghm-pdf" className="rounded-full text-xs"><FileText size={13} className="mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => exp("xlsx")} data-testid="soins-ghm-xlsx" className="rounded-full text-xs"><FileSpreadsheet size={13} className="mr-1" /> Excel</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="px-3 py-2">Code GHM</th><th className="px-3 py-2">Libellé</th><th className="px-3 py-2">Tarif GHS (FCFA)</th><th className="px-3 py-2">Borne basse</th><th className="px-3 py-2">Borne haute</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-2 py-1"><Input value={r.code_ghm} onChange={(e) => upd(i, "code_ghm", e.target.value)} data-testid={`ghm-code-${i}`} className="h-8 text-xs" /></td>
                <td className="px-2 py-1"><Input value={r.libelle} onChange={(e) => upd(i, "libelle", e.target.value)} data-testid={`ghm-lib-${i}`} className="h-8 text-xs" /></td>
                <td className="px-2 py-1"><Input type="number" value={r.ghs_fcfa} onChange={(e) => upd(i, "ghs_fcfa", +e.target.value)} data-testid={`ghm-tarif-${i}`} className="h-8 w-32 text-xs" /></td>
                <td className="px-2 py-1"><Input type="number" value={r.borne_basse} onChange={(e) => upd(i, "borne_basse", +e.target.value)} className="h-8 w-20 text-xs" /></td>
                <td className="px-2 py-1"><Input type="number" value={r.borne_haute} onChange={(e) => upd(i, "borne_haute", +e.target.value)} className="h-8 w-20 text-xs" /></td>
                <td className="px-2 py-1 text-right"><button onClick={() => delRow(i)} className="rounded p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SoinsDocsManager() {
  const [schemas, setSchemas] = useState(null);
  const [certType, setCertType] = useState("compte_rendu");
  useEffect(() => { api.get("/admin/soins/schemas").then(({ data }) => setSchemas(data.schemas)).catch(() => {}); }, []);

  if (!schemas) return <div className="flex h-32 items-center justify-center"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>;

  return (
    <div data-testid="soins-docs-manager">
      <div className="mb-4 flex items-center gap-2 rounded-xl bg-[#0E7C7B]/10 px-4 py-3 text-[#0b6463]">
        <Stethoscope size={18} />
        <span className="text-sm font-semibold">Certificats médicaux — Cabinet CARIS Santé</span>
        <span className="ml-auto text-[11px] font-normal">Le PMSI (RUM, RSS, RSA, GHM/GHS) est désormais dans le module « PMSI & Couverture »</span>
      </div>
      <Tabs defaultValue="certificats">
        <TabsList className="mb-4 flex-wrap bg-slate-100">
          <TabsTrigger value="certificats" data-testid="soins-tab-certificats">Certificats médicaux</TabsTrigger>
        </TabsList>
        <TabsContent value="certificats">
          <div className="mb-3 max-w-md">
            <Label className="text-[11px] font-medium text-slate-500">Type de certificat</Label>
            <Select value={certType} onValueChange={setCertType}>
              <SelectTrigger className="mt-1 h-10" data-testid="soins-cert-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CERT_ORDER.map((k) => <SelectItem key={k} value={k}>{schemas[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DocSection key={certType} doctype={certType} schema={schemas[certType]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
